// raisedbeef.com `/` — the Cattle Ticker homepage.
//
// Built to design/Main.dc.html, the approved 1440×900 artboard in the
// "RaisedBeef Homepage" design canvas. That artboard is the spec: where this
// page and the artboard disagree, the artboard is right and this is a bug.
//
// ⚠️ The first version of this file reused the shared rbnext Header,
// FeaturedStrip and ToolGrid instead of building the artboard, and the result
// looked nothing like what was approved — a white/green tools page with a navy
// panel dropped into it. This page has its OWN chrome, its own statement band,
// its own picks strip and its own six-tool rail for exactly that reason. It
// shares nothing visual with the other two homepages beyond the brand marks.
//
// ── Layout, per the artboard ──────────────────────────────────────────────
//   chrome        navy masthead, both marks, five-item nav, "Are you a sale
//                 barn?", gold Sign in
//   left          the ticker: head + filters, the DVAuction statement band,
//                 the 4×2 board of weight bands
//   picks menu    a gold secondary menu under the masthead — admin-curated
//                 per weekday (was a strip under the board until 2026-09-13)
//   rail          INDEXES, then THE PACKER SIDE
//
// ── The three rules this file must obey ───────────────────────────────────
// It is prerendered to static HTML at build time and hydrated by
// src/entry-rbticker.tsx. Breaking any of these either fails the build or
// produces a hydration mismatch in production:
//
//  1. No `window` / `document` / `localStorage` at module scope or during
//     render. Anything importing @/integrations/supabase/client throws under
//     Node, because that module touches localStorage while loading.
//  2. No data fetching during render. Figures arrive as props, baked in by the
//     prerender; the client refresh happens in an effect, after mount.
//  3. No react-router <Link>. This document mounts no Router — a <Link> would
//     push history and render nothing. Plain <a> only.

import { useEffect, useState } from "react";

import {
  EMPTY_RAIL,
  EMPTY_TICKER,
  fetchRail,
  type RailPayload,
  type TickerBarn,
  type TickerPayload,
} from "@/lib/rbCattleTicker";
import { fetchRbTools, RB_TOOLS_FALLBACK, type RbToolsPayload } from "@/lib/rbTools";

import TickerBoard from "./cattle-ticker/TickerBoard";
import DailyPriceBoard from "./index-page/DailyPriceBoard";
import DailyPriceGrid from "./index-page/DailyPriceGrid";
import TickerChrome from "./cattle-ticker/TickerChrome";
import { selectPicks } from "./cattle-ticker/selectPicks";
import {
  type RbIndexLive,
  type RbIndexTile,
  fetchRbIndexLive,
  fetchRbIndexPublic,
  fetchRbIndexes,
} from "@/lib/rbIndexes";
import { useIndexAccess } from "@/lib/indexAccess";
import LivePricesLocked from "./cattle-ticker/LivePricesLocked";
import { type FeederFlashEpisode, fetchFeederFlash } from "@/lib/rbFeederFlash";
import type { HomeKpi } from "@/lib/rbHomeKpis";
import { SaleHighlights } from "./cattle-ticker/SaleHighlights";
import { FeederFlashCard, PicksMenu, RbIndexCards, TickerRail } from "./cattle-ticker/TickerParts";

/** ⚠️ TODAY'S PICKS AND THE PACKER SIDE ARE HIDDEN, NOT DELETED (owner,
 *  2026-10-14: "can we just hide the today's picks, boxed beef, and fed cattle
 *  for now? we need to find out where they will go"). Sale Highlights took the
 *  sidebar. Their fetches are off too. Set true to bring both back as they were. */
const SHOW_PICKS_AND_PACKER_SIDE = false;

// The display + label faces the artboard is set in. Self-hosted rather than
// Google Fonts: this is the front door, and a third-party font request in the
// critical path is a render-blocking dependency on someone else's CDN.
import "@fontsource/oswald/500.css";
import "@fontsource/oswald/600.css";
import "@fontsource/oswald/700.css";
import Footer from "./Footer";
import "./rbnext.css";
import "./cattle-ticker/tickerHome.css";


interface Props {
  initialKpis?: HomeKpi[];
  initialFeederFlash?: FeederFlashEpisode | null;
  /** The seven RB cattle indexes, baked by the prerender so the rail is not
   *  empty in the served HTML. */
  initialRbIndexes?: RbIndexTile[];
  /** Today's barns + receipts, baked by the prerender so the rail is not
   *  empty in the served HTML. Client refreshes it on the 30s tick. */
  initialBarns?: TickerBarn[];
  initialTools?: RbToolsPayload;
  /** The tape, baked in by the prerender. Rendered on the first client pass
   *  too, so the hydrated tree matches the served HTML exactly. */
  initialTicker?: TickerPayload;
  /** The rail's three figures, baked in for the same reason. */
  initialRail?: RailPayload;
}

/** ⚠️ PAIRED WITH /api/market's ttl FOR rb_index_live (20s). Change one, change
 *  the other — a poll faster than the cache just re-fetches what it has.
 *
 *  ⚠️ 20s, AND THE FIRST MEASUREMENT BEHIND IT WAS WRONG. Consecutive rows
 *  arrive ~3.8s apart, but those are rows inside one ingest batch and say
 *  nothing about how often new data appears. Measured properly on 2026-10-05:
 *  during a sale day 56-60 minutes out of every 60 receive new rows, and the
 *  median gap between minutes-with-new-data is exactly 60 seconds. So the data
 *  changes every minute, and 20s keeps the worst-case lag at 40s — inside the
 *  minute, which is what the owner asked for. */
const RB_LIVE_POLL_MS = 20_000;

export default function RbTickerHome({
  initialKpis,
  initialFeederFlash,
  initialRbIndexes,
  initialBarns,
  initialTools,
  initialTicker,
  initialRail,
}: Props) {
  // ⚠️ AN EARLIER INDEXES RAIL WAS REMOVED (owner, 2026-09-25). Two of its
  // three tiles — NBW Real-Time Index and NBW Steer Tracker — were the NBW feed,
  // dead since 2026-07-26 (ACQUISITION_ROADMAP 3g), and the third was CME,
  // which is synthetic placeholder data with no live ingest. So the front door
  // of the company carried three figures reading "as of Jul 23/24/26" into
  // late September. The as-of stamps were honest and did their job; a tile
  // that has to explain why it is two months stale is not earning its place.
  //
  // `initialKpis` is still accepted, and fetchHomeKpis/rbHomeKpis.ts are
  // untouched: RbNextHome still renders them, and this rail goes back the day
  // the NBW feed is repaired. Nothing is deleted, only unmounted here.
  // An empty baked lineup is kept as empty — it means nothing is Popular, and
  // this page has no tool grid to fill, only the picks menu (see usable() in
  // rbTools.ts for the six phantom buttons the fallback used to draw here).
  const [tools, setTools] = useState<RbToolsPayload>(initialTools ?? RB_TOOLS_FALLBACK);
  /** Seeded from the prerender, then refreshed after mount — the document is
   *  served until the next deploy and Feeder Flash posts daily, so the baked
   *  episode goes stale in a day while the page itself does not. */
  const [feederFlash, setFeederFlash] = useState<FeederFlashEpisode | null>(
    initialFeederFlash ?? null,
  );
  /** Seeded from the prerender: the served HTML and the first client render
   *  have to agree or React throws on hydration. */
  const [rbIndexes, setRbIndexes] = useState<RbIndexTile[]>(initialRbIndexes ?? []);
  /** ⚠️ NOT SEEDED FROM THE PRERENDER, unlike the settled figures above. This
   *  document is served until the next deploy — days — and a baked "live" price
   *  would be a stale number wearing a pulsing dot, which is worse than no row
   *  at all. It arrives on mount and the row does not exist before then. */
  const [rbLive, setRbLive] = useState<Record<string, RbIndexLive>>({});
  /** ⚠️ WHEN WE LAST HEARD BACK, not when a figure last changed. The panel is
   *  live even on a quiet market, and that is exactly when a reader needs to
   *  know the feed is still running. Kept separate from rbLive because that
   *  state deliberately does not update when the payload is identical. */
  const [rbLiveAt, setRbLiveAt] = useState<number | null>(null);
  /** Premium, the free 30 days, or an admin see the prices; everyone else the
   *  locked cards (Premium lock, 2026-10-13). The prerender bakes the locked
   *  cards — it is nobody — and a member's prices replace them once this
   *  resolves. Null until it does. */
  const ixAccess = useIndexAccess();
  const ixOpen = ixAccess.state === "loading" ? null : ixAccess.state === "open";
  /** Null on the server and on the first client render, by design: the build
   *  machine's day is not the visitor's, and this one document is served all
   *  week. The picks menu reserves its space and fills the weekday in after
   *  mount rather than dropping into the page and shoving the rest down. */
  const [weekday, setWeekday] = useState<string | null>(null);
  /** Seeded from the prerender so the first client render matches the served
   *  HTML exactly, then refreshed in an effect. Without the seed, three cards
   *  each grow ~70px a beat after first paint and shove the rail around. */
  const [rail, setRail] = useState<RailPayload>(initialRail ?? EMPTY_RAIL);

  // Correct the lineup for the VISITOR'S day, after mount and never during
  // render. The date is built from LOCAL parts rather than toISOString(), which
  // would hand the server yesterday's date for anyone behind UTC and feature
  // the wrong day's tools.
  useEffect(() => {
    if (!SHOW_PICKS_AND_PACKER_SIDE) return;
    let live = true;
    const now = new Date();
    const p = (n: number) => String(n).padStart(2, "0");
    const iso = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
    setWeekday(now.toLocaleDateString("en-US", { weekday: "long" }));

    fetchRbTools(iso, now.getDay())
      .then((next) => {
        // No tools-length guard: a day with no picks is a real answer.
        if (!live) return;
        setTools((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      })
      .catch(() => {
        /* keep whatever was prerendered */
      });
    return () => { live = false; };
  }, []);

  // ── Today's trade, which DOES have to move ─────────────────────────────────
  // ⚠️ THE OPPOSITE CADENCE TO THE SETTLED BOARD BELOW, and for the opposite
  // reason. That reads a snapshot written once a night and cannot change. This
  // reads TODAY: measured over 14 days, a sale day's lots arrive steadily from
  // 9am to 6pm Central — 10.5% in by 9, 50.9% by noon, 95.3% by 5pm. Rob asked
  // for a price "people can watch live from morning till night", and a number
  // nobody is told has changed is not one.
  //
  // ⚠️ 60s IS PAIRED WITH /api/market's ttl FOR rb_index_live. At a 60s TTL
  // against a 30s poll, every other request spends a round trip to be handed
  // the number it already had — the mistake the DVA panel documented before it
  // was retired. Change one, change the other.
  //
  // Gated on the tab being visible: a homepage left open overnight would
  // otherwise poll ~700 times to redraw a row nobody is looking at.
  useEffect(() => {
    if (ixOpen === null) return;
    let live = true;
    const load = () => {
      // Locked: the public board is both halves at once — cards and today —
      // with no price in either.
      (ixOpen ? fetchRbIndexLive() : fetchRbIndexPublic().then((pub) => {
        if (live && pub.tiles.length) {
          setRbIndexes((prev) => (JSON.stringify(prev) === JSON.stringify(pub.tiles) ? prev : pub.tiles));
        }
        return pub.live;
      }))
        .then((next) => {
          if (!live) return;
          setRbLiveAt(Date.now());
          setRbLive((prev) =>
            JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
          );
        })
        .catch(() => {
          /* keep the last good reading rather than blanking a live price */
        });
    };
    load();
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (timer === null) timer = setInterval(load, RB_LIVE_POLL_MS); };
    const stop = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
    const onVisibility = () => {
      if (document.visibilityState === "visible") { load(); start(); } else stop();
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      live = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [ixOpen]);

  // ── The RB indexes are corrected once, not polled ──────────────────────────
  // ⚠️ DELIBERATELY NOT THE 30s POLL ABOVE, AND THE REASON IS THE DATA, NOT THE
  // COST. A published index is written ONCE A NIGHT by rb-index-snapshot-nightly
  // and cannot change until the next run — polling it would spend a round trip
  // every thirty seconds to be handed the same row.
  //
  // ⚠️ BUT IT CANNOT BE LEFT ON THE BAKED VALUE EITHER. This document is
  // PRERENDERED and served until the next deploy, which may be days. The figures
  // in the HTML are from build time, so without this the rail could quietly show
  // last week's index behind a page that looks current. One fetch on mount, and
  // again when the tab comes back, is exactly enough.
  useEffect(() => {
    // Only for those who see prices; the locked cards come with the poll above.
    if (ixOpen !== true) return;
    let live = true;
    const load = () => {
      fetchRbIndexes()
        .then((next) => {
          // The length guard keeps a transient empty response from blanking
          // good tiles. An empty answer is not news that the market vanished.
          if (!live || !next.length) return;
          setRbIndexes((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
        })
        .catch(() => {
          /* keep the baked figures rather than blanking the rail */
        });
    };
    load();
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      live = false;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [ixOpen]);


  useEffect(() => {
    let live = true;
    fetchFeederFlash()
      .then((next) => { if (live && next) setFeederFlash(next); })
      .catch(() => {
        /* keep whatever was prerendered — a missing episode is not an error */
      });
    if (SHOW_PICKS_AND_PACKER_SIDE) void fetchRail().then((next) => {
      // Skip an identical update — the cutout publishes twice a day and the
      // 5-Area weekly once, so the overwhelming case is no change, and
      // re-setting identical state would repaint the rail for nothing.
      if (live) setRail((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    });
    return () => { live = false; };
  }, []);

  // Exactly the day's schedule from /manage/tools — no filler (owner,
  // 2026-09-13). A day with nothing scheduled has no gold bar. See selectPicks.
  const { picks, curated } = selectPicks(tools.tools);

  return (
    <div className="rbtx">
      <TickerChrome hero />
      {/* Owner, 2026-10-08, from a design mock: the six headline indexes go
          across the top under the masthead, as cards, and the day's picks
          take the sidebar spot they leave. The picks were a gold bar here
          (2026-09-13, "so it is not missed"); the sidebar card is still gold. */}
      <RbIndexCards tiles={rbIndexes} live={rbLive} updatedAt={rbLiveAt} variant="band" cta={ixAccess.state} />

      <div className="rbtx-stage rbtx-stage-home">
        <div className="rbtx-left">
          {/* Owner, 2026-10-07: the Daily Live Price board goes "in this
              empty spot under the ticker between the left and right sidebar"
              — under the price bands, in their column, beside the barn rail.
              Client-only; renders nothing until it has rows. */}
          {/* Live Prices shows the daily table and, under it, the weekly grid
              (owner, 2026-10-08: "i want this to live on the Daily live price
              toggle above the current table we have on there"). Nothing sits
              under the bands in the Ticker view any more. */}
          {/* Signed out: Cattle Ticker. Signed in: Live Prices (owner, 2026-11-03). "signup" is the
              stranger; "open" and "expired" are both signed in. */}
          <TickerBoard initial={initialTicker ?? EMPTY_TICKER} liveRail initialBarns={initialBarns}
                       defaultView={ixAccess.state === "loading" ? null : ixAccess.state === "signup" ? "board" : "live"}
                       liveView={ixOpen ? <><DailyPriceBoard /><DailyPriceGrid /></>
                         // While the plan is read: the tab stays (it is in the
                         // served HTML) and its panel waits, empty.
                         : ixOpen === false ? <LivePricesLocked cta={ixAccess.state} /> : <></>} />
        </div>

        <aside className="rbtx-rail" aria-label="Markets">
          {/* EVEN COLUMNS (owner, 2026-10-25): the fill takes the board's row
              height (tickerHome.css); Feeder Flash, a fixed-size card, sits on
              top where it is never cut off, and the highlights feed takes
              whatever height is left and scrolls. Until 2026-10-25 the feed
              had a fixed height and Feeder Flash sat under it. */}
          <div className="rbtx-rail-fill">
            {SHOW_PICKS_AND_PACKER_SIDE && <PicksMenu weekday={weekday} picks={picks} curated={curated} variant="rail" />}
            <FeederFlashCard episode={feederFlash} />
            <SaleHighlights />
            {SHOW_PICKS_AND_PACKER_SIDE && <TickerRail boxed={rail.boxed} fiveArea={rail.fiveArea} margin={rail.margin} />}
          </div>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
